import type { ServiceContext } from "@prolarus/shared";
import { apiPost, ApiError } from "@/lib/apiClient";
import { tryGet, tryList, tryUpdate } from "./_shared";

export type SessionType = "briefing" | "validation";
export type ChatEntityType = "price_book";

export interface ChatSession {
  id: string;
  company_id: string;
  entity_type: ChatEntityType;
  entity_id: string;
  session_type: SessionType;
  conversation_history: Array<{ role: string; content: string; id: string; timestamp: string }>;
  session_status: string;
  created: string;
  modified: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface ChatSessionSummary {
  id: string;
  modified: string;
  updated_by_profile: { full_name: string } | null;
}

export const chatSessionService = {
  getActiveByEntity(entityType: ChatEntityType, entityId: string, sessionType: SessionType = "briefing") {
    return tryGet<ChatSession>(
      `/api/chat-sessions/active?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}&sessionType=${encodeURIComponent(sessionType)}`,
    );
  },
  listByEntity(entityType: ChatEntityType, entityId: string, sessionType: SessionType = "briefing") {
    return tryList<ChatSessionSummary>(
      `/api/chat-sessions?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}&sessionType=${encodeURIComponent(sessionType)}`,
    );
  },
  getById(id: string) {
    return tryGet<ChatSession>(`/api/chat-sessions/${encodeURIComponent(id)}`);
  },
  async create(
    entityType: ChatEntityType,
    entityId: string,
    _context: ServiceContext,
    sessionType: SessionType = "briefing",
  ): Promise<{ data: ChatSession | null; error: Error | null }> {
    try {
      const result = await apiPost<{ data: ChatSession }>("/api/chat-sessions", {
        entity_type: entityType,
        entity_id: entityId,
        session_type: sessionType,
      });
      return { data: result.data, error: null };
    } catch (err) {
      const e = err instanceof ApiError ? err : err instanceof Error ? err : new Error(String(err));
      return { data: null, error: e };
    }
  },
  updateConversation(
    id: string,
    history: Array<{ role: string; content: string; id: string; timestamp: string }>,
    _context: ServiceContext,
  ) {
    return tryUpdate<ChatSession>(`/api/chat-sessions/${encodeURIComponent(id)}/conversation`, {
      conversation_history: history,
    });
  },
  updateStatus(
    id: string,
    status: "active" | "completed" | "abandoned",
    _context: ServiceContext,
  ) {
    return tryUpdate<ChatSession>(`/api/chat-sessions/${encodeURIComponent(id)}/status`, {
      session_status: status,
    });
  },
};
